import { TriangleIcon } from "@imi/icons/TriangleIcon.js";
import {
  ChangeEvent,
  ComponentPropsWithoutRef,
  ElementType,
  RefObject,
} from "react";
import { useFieldContext } from "../../hooks/useAppForm";
import { useIdOr } from "../../hooks/useIdOr";
import { FieldError, FieldErrors } from "../FieldErrors/FieldErrors";
import styles from "./TextField.module.css";

export type TextFieldAs = "input" | "textarea";

export type TextFieldProps<As extends TextFieldAs> = {
  as?: As;
  errors?: FieldError;
  label?: string;
  labelId?: string;
  ref?: RefObject<HTMLLabelElement | null>;
  suffix?: string | null;
} & ComponentPropsWithoutRef<As>;

export function TextField<As extends TextFieldAs>({
  as,
  className,
  errors,
  label,
  labelId,
  ref,
  style,
  suffix,
  ...props
}: TextFieldProps<As>) {
  const Component = (as || "input") as ElementType;
  const hasErrors = errors && (Array.isArray(errors) ? !!errors.length : true);
  const id = useIdOr(props.id);
  const errorId = hasErrors ? `${id}-error` : undefined;

  return (
    <FieldErrors
      className={className}
      data-slot="control"
      errorId={errorId}
      errors={errors}
    >
      <label
        className={styles.TextFieldLabel}
        htmlFor={id}
        id={labelId}
        ref={ref}
        style={style}
      >
        {label && (
          <span>
            {label}
            {hasErrors && <TriangleIcon data-slot="error-icon" />}
          </span>
        )}
        <Component
          aria-describedby={errorId || undefined}
          aria-invalid={hasErrors || undefined}
          data-autofocus={props.autoFocus || undefined}
          {...(props as ComponentPropsWithoutRef<typeof Component>)}
          id={id}
        />
        {suffix ? <span data-slot="suffix">{suffix}</span> : null}
      </label>
    </FieldErrors>
  );
}

export function TextFieldField<As extends TextFieldAs>(
  props: TextFieldProps<As>,
) {
  const field = useFieldContext<string>();
  return (
    <TextField
      errors={field.state.meta.errors.map((e) =>
        typeof e === "string" ? e : e?.message,
      )}
      {...props}
      form={field.form.formId}
      onBlur={field.handleBlur}
      onChange={(e: ChangeEvent<HTMLInputElement, HTMLInputElement>) =>
        field.handleChange(e.currentTarget.value)
      }
      value={field.state.value}
    />
  );
}
